import ViewQuestion from "@/components/ViewQuestion/ViewQuestion";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { Card, List, Pagination } from "@/components/ui";
import { IEdoQuestion } from "@/shared/model/edo-question.model";
import { getQuestionsByAccount } from "@/shared/reducers/entities/edo-question.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { ITEMS_PER_PAGE } from "@/shared/util/pagination.constants";
import { HelpCircle, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { IPaginationBaseState, Translate } from "react-jhipster";
import EditQuestion from "../../components/EditQuestion/EditQuestion";
import DeleteQuestion from "./components/DeleteQuestion";
import QuestionRow from "./components/QuestionRow";

const QuestionsRoute = () => {
  const dispatch = useAppDispatch();
  const { entities: questions, loading, totalItems, updateSuccess } =
    useAppSelector<EntityState<IEdoQuestion>>((state) => state.edoQuestion);
  const [viewQuestionDialog, setViewQuestionDialog] = useState<{
    open: boolean;
    entity: IEdoQuestion | null;
  }>({ open: false, entity: null });
  const [editQuestionDialog, setEditQuestionDialog] = useState<{
    open: boolean;
    entity: IEdoQuestion | null;
  }>({ open: false, entity: null });
  const [deleteQuestionDialog, setDeleteQuestionDialog] = useState<{
    open: boolean;
    entity: IEdoQuestion | null;
  }>({ open: false, entity: null });
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "updatedAt",
    order: "DESC",
  });

  const getAllQuestions = () => {
    dispatch(
      getQuestionsByAccount({
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  const handlePagination = (currentPage: number) =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  useEffect(() => {
    getAllQuestions();
  }, [
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  useEffect(() => {
    if (updateSuccess) {
      getAllQuestions();
    }
  }, [updateSuccess]);

  return (
    <>
      <Card className="overflow-hidden rounded-3xl border border-[var(--color-mockup-border)] shadow-card bg-surface p-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] p-4 bg-[var(--color-surface-secondary)]/30">
          <h2 className="text-lg font-bold text-[var(--color-mockup-text)]">Domande</h2>
          <button
            type="button"
            onClick={() => setEditQuestionDialog({ open: true, entity: null })}
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-mockup-teal)] px-4 py-2 text-xs font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95"
            aria-label="Aggiungi domanda"
          >
            <Plus className="h-4 w-4" />
            Aggiungi domanda
          </button>
        </div>
        {loading && (
          <div className="flex justify-center py-8">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}
        {questions?.length > 0 ? (
          <List className="divide-y divide-[var(--color-mockup-border)]">
            {questions.map((question) => (
              <QuestionRow
                key={question.id}
                question={question}
                onView={() =>
                  setViewQuestionDialog({ open: true, entity: question })
                }
                onEdit={() =>
                  setEditQuestionDialog({ open: true, entity: question })
                }
                onDelete={() =>
                  setDeleteQuestionDialog({ open: true, entity: question })
                }
              />
            ))}
          </List>
        ) : (
          !loading && (
            <div className="px-6 py-12 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <HelpCircle className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                <Translate contentKey="edocendoApp.edoQuestion.home.notFound" />
              </p>
            </div>
          )
        )}
        <Pagination
          count={totalItems}
          page={paginationState.activePage - 1}
          rowsPerPage={paginationState.itemsPerPage}
          onPageChange={(p) => handlePagination(p + 1)}
          onRowsPerPageChange={handleRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} di ${count !== -1 ? count : "more than" + to}`
          }
        />
      </Card>
      <ViewQuestion
        open={viewQuestionDialog.open}
        question={viewQuestionDialog.entity}
        onClose={() => setViewQuestionDialog({ open: false, entity: null })}
      />
      <EditQuestion
        open={editQuestionDialog.open}
        question={editQuestionDialog.entity ?? undefined}
        onClose={() => setEditQuestionDialog({ open: false, entity: null })}
      />
      <DeleteQuestion
        open={deleteQuestionDialog.open}
        question={deleteQuestionDialog.entity}
        onClose={() => setDeleteQuestionDialog({ open: false, entity: null })}
      />
    </>
  );
};

export default QuestionsRoute;
